import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { mergePageCountLimits, PageCountLimit } from 'editor/src/store/design/util/pageCountControls';
import { useSelector } from 'editor/src/store/hooks';
import isPageCountOptionsEnabled from 'editor/src/store/hostSettings/selector/isPageCountOptionsEnabled';
import { ECOMMERCE_PAGE_COUNT_CONTROL_NAME } from 'editor/src/store/variants/constants';
import getECommercePageCounts from 'editor/src/store/variants/selector/getECommercePageCounts';
import getECommerceProduct from 'editor/src/store/variants/selector/getECommerceProduct';

// Until the host sends `enablePageCountOptions`: the design's window narrowed to the lowest and
// highest page count sold on the option named exactly "Page count".
function useLegacyPageCountLimit(enabled: boolean): PageCountLimit | undefined {
  const designPageCountLimit = useSelector((state) => state.design.designData?.page_count_limit);
  const ecommerceProduct = useSelector(getECommerceProduct);
  const ecommercePageCountLimit = useMemo(() => {
    if (enabled || !ecommerceProduct) {
      return undefined;
    }

    const pageCountControlIndex = ecommerceProduct.options.findIndex(
      (option) => option.name === ECOMMERCE_PAGE_COUNT_CONTROL_NAME,
    );
    if (pageCountControlIndex === -1) {
      return undefined;
    }

    const sortedPagesCount = ecommerceProduct.variants
      .map((variant) => parseInt(variant.optionValues[pageCountControlIndex], 10))
      .sort((a, b) => a - b);
    return { min: sortedPagesCount[0], max: sortedPagesCount[sortedPagesCount.length - 1] };
  }, [ecommerceProduct, enabled]);

  return useMemo<PageCountLimit | undefined>(() => {
    if (designPageCountLimit && ecommercePageCountLimit) {
      return {
        min: Math.max(designPageCountLimit.min, ecommercePageCountLimit.min),
        max: Math.min(designPageCountLimit.max, ecommercePageCountLimit.max),
        increment: designPageCountLimit.increment,
      };
    }

    return designPageCountLimit || ecommercePageCountLimit;
  }, [designPageCountLimit, ecommercePageCountLimit]);
}

function usePageCountLimit() {
  const enabled = useSelector(isPageCountOptionsEnabled);
  const designPageCountLimit = useSelector((state) => state.design.designData?.page_count_limit);
  const ecommercePageCounts = useSelector(getECommercePageCounts, shallowEqual);
  const legacyPageCountLimit = useLegacyPageCountLimit(enabled);

  const pageCountLimit = useMemo<PageCountLimit | undefined>(
    () => mergePageCountLimits(designPageCountLimit, ecommercePageCounts),
    [designPageCountLimit, ecommercePageCounts],
  );
  return enabled ? pageCountLimit : legacyPageCountLimit;
}

export default usePageCountLimit;
